/**
 * FrontDesk WebSocket Client
 * 
 * This module handles connecting to the WebSocket server for real-time updates
 * to the FrontDesk view. It will automatically reconnect if the connection is lost.
 */

import aiopWebSocket from './aiopWebSocketClient';

/**
 * Production runs on App Runner, whose proxy rejects WebSocket upgrades, so production
 * builds receive these notifications over the tab's shared server-sent-events stream
 * (aiopWebSocketClient) instead. REACT_APP_REALTIME_TRANSPORT=ws|sse overrides this.
 * Dev keeps the WebSocket and falls back to SSE if the upgrade never succeeds.
 */
function prefersServerSentEvents() {
  const transport = process.env.REACT_APP_REALTIME_TRANSPORT;
  if (transport === 'sse') return true;
  if (transport === 'ws') return false;
  return process.env.NODE_ENV === 'production';
}

/**
 * The WebSocket server lives on the backend, which isn't on the page's origin when the
 * frontend is hosted separately (Amplify), so derive the URL from REACT_APP_API_URL.
 * Without it, keep the old same-host behavior (dev talks to :4000).
 */
function getWebSocketUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    try {
      const url = new URL(apiUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = `${url.pathname.replace(/\/+$/, '')}/ws`;
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch (error) {
      console.error('[WS-CLIENT] Invalid REACT_APP_API_URL, falling back to page host:', error);
    }
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const port = process.env.NODE_ENV === 'production' ? '' : ':4000';
  return `${protocol}//${window.location.hostname}${port}/ws`;
}

class FrontDeskWebSocketClient {
  constructor(refreshDebounceTime = 3000) {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;  // Add connecting state to prevent multiple connections
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    // If the server never accepts the upgrade (App Runner's proxy answers 403), stop early
    // instead of retrying for the whole page session; pages still refresh by polling.
    this.hasEverConnected = false;
    this.maxAttemptsBeforeFirstConnect = 2;
    this.reconnectInterval = 5000; // Increase to 5 seconds
    this.pingInterval = null;  // For keeping connection alive
    this.refreshDebounceTime = refreshDebounceTime; // Configurable debounce time
    this.useServerSentEvents = prefersServerSentEvents();
    this.unsubscribeServerSentEvents = null;
    this.callbacks = {
      onRefresh: [],  // Array to support multiple callbacks
      onConnect: [],
      onDisconnect: []
    };
  }

  /**
   * Connect to the WebSocket server
   */
  connect() {
    if (this.useServerSentEvents) {
      this.connectServerSentEvents();
      return;
    }

    // Prevent multiple connection attempts
    if (this.isConnecting) {
      return;
    }

    // Close any existing connection
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.isConnecting = true;

    try {
      this.socket = new WebSocket(getWebSocketUrl());

      // Set up event handlers
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('[WS-CLIENT] Error creating WebSocket:', error);
      this.isConnecting = false;
      this.attemptReconnect();
    }
  }

  /**
   * Receive the notifications over the tab's shared SSE stream. The stream reconnects
   * on its own, so this subscribes once.
   */
  connectServerSentEvents() {
    if (this.unsubscribeServerSentEvents) return;

    this.isConnecting = true;
    this.unsubscribeServerSentEvents = aiopWebSocket.subscribeFrontDesk({
      onEvent: (data) => this.dispatchMessage(data),
      onConnect: () => this.handleOpen(),
      onDisconnect: () => this.handleServerSentEventsClose(),
    });
  }

  handleServerSentEventsClose() {
    this.isConnected = false;
    this.isConnecting = true; // the shared stream is reconnecting
    this.callbacks.onDisconnect.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[WS-CLIENT] Error in disconnect callback:', error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect() {
    if (this.unsubscribeServerSentEvents) {
      this.unsubscribeServerSentEvents();
      this.unsubscribeServerSentEvents = null;
      this.isConnected = false;
      this.isConnecting = false;
    }

    if (this.socket) {
      console.log('[WS-CLIENT] Disconnecting from WebSocket server');

      // Clear ping interval if it exists
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }

      // Only call close if socket exists and is not already closed
      if (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }

      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
    }
  }

  /**
   * Handle WebSocket open event
   */
  handleOpen() {
    this.isConnected = true;
    this.isConnecting = false;
    this.hasEverConnected = true;
    this.reconnectAttempts = 0;

    // Set up ping interval to keep connection alive (WebSocket only)
    if (this.useServerSentEvents) {
      this.callbacks.onConnect.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('[WS-CLIENT] Error in connect callback:', error);
        }
      });
      return;
    }
    this.pingInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Send a small ping message to keep the connection alive
        this.socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Every 30 seconds

    // Call all connect callbacks
    this.callbacks.onConnect.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[WS-CLIENT] Error in connect callback:', error);
      }
    });
  }

  /**
   * Handle WebSocket close event
   */
  handleClose(event) {
    // Check if it was a normal closure or abnormal
    const wasClean = event && event.wasClean;

    console.log(`[WS-CLIENT] Disconnected from WebSocket server${wasClean ? ' (clean)' : ''}`);

    // Clear ping interval
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    this.isConnected = false;
    this.isConnecting = false;

    // Call all disconnect callbacks
    this.callbacks.onDisconnect.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[WS-CLIENT] Error in disconnect callback:', error);
      }
    });

    // Only attempt reconnect for abnormal closures
    if (!wasClean) {
      this.attemptReconnect();
    }
  }

  /**
   * Handle WebSocket message event
   * @param {Object} event - WebSocket message event
   */
  handleMessage(event) {
    try {
      this.dispatchMessage(JSON.parse(event.data));
    } catch (error) {
      console.error('[WS-CLIENT] Error handling message:', error);
    }
  }

  /**
   * Route a parsed notification (from the WebSocket or the SSE stream) to subscribers
   * @param {Object} data
   */
  dispatchMessage(data) {
    try {
      switch (data.type) {
        case 'connected':
          break;

        case 'refresh':
        case 'immediate-refresh':
          // Call all refresh callbacks
          this.callbacks.onRefresh.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('[WS-CLIENT] Error in refresh callback:', error);
            }
          });
          break;

        case 'pong':
          // Silently handle pong responses
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('[WS-CLIENT] Error handling message:', error);
    }
  }

  /**
   * Handle WebSocket error event
   * @param {Object} error - WebSocket error
   */
  handleError(error) {
    console.error('[WS-CLIENT] WebSocket error:', error);
    this.isConnecting = false;

    // The close event will be called automatically after an error
    // We don't need to disconnect or attempt reconnect here
  }

  /**
   * Attempt to reconnect to the WebSocket server
   */
  attemptReconnect() {
    const maxAttempts = this.hasEverConnected
      ? this.maxReconnectAttempts
      : this.maxAttemptsBeforeFirstConnect;
    if (this.reconnectAttempts < maxAttempts) {
      this.reconnectAttempts++;

      const delay = this.reconnectInterval * Math.min(this.reconnectAttempts, 3);
      console.log(`[WS-CLIENT] Attempting to reconnect (${this.reconnectAttempts}/${maxAttempts}) in ${delay}ms...`);

      setTimeout(() => {
        if (!this.isConnected && !this.isConnecting) {
          this.connect();
        }
      }, delay);
    } else if (!this.hasEverConnected) {
      console.warn('[WS-CLIENT] WebSocket unavailable, using server-sent events');
      this.useServerSentEvents = true;
      this.connectServerSentEvents();
    } else {
      console.error('[WS-CLIENT] Max reconnect attempts reached, giving up');
    }
  }

  /**
   * Add callback for refresh events
   * @param {Function} callback - Function to call when refresh event is received
   * @returns {Function} - Cleanup function to remove the callback
   */
  onRefresh(callback) {
    if (callback) {
      this.callbacks.onRefresh.push(callback);

      // Return cleanup function
      return () => {
        const index = this.callbacks.onRefresh.indexOf(callback);
        if (index > -1) {
          this.callbacks.onRefresh.splice(index, 1);
        }
      };
    } else {
      // Legacy behavior - clear all callbacks if null is passed
      this.callbacks.onRefresh = [];
    }
  }

  /**
   * Add callback for connect events
   * @param {Function} callback - Function to call when connection is established
   * @returns {Function} - Cleanup function to remove the callback
   */
  onConnect(callback) {
    if (callback) {
      this.callbacks.onConnect.push(callback);
      return () => {
        const index = this.callbacks.onConnect.indexOf(callback);
        if (index > -1) {
          this.callbacks.onConnect.splice(index, 1);
        }
      };
    } else {
      this.callbacks.onConnect = [];
    }
  }

  /**
   * Add callback for disconnect events
   * @param {Function} callback - Function to call when connection is lost
   * @returns {Function} - Cleanup function to remove the callback
   */
  onDisconnect(callback) {
    if (callback) {
      this.callbacks.onDisconnect.push(callback);
      return () => {
        const index = this.callbacks.onDisconnect.indexOf(callback);
        if (index > -1) {
          this.callbacks.onDisconnect.splice(index, 1);
        }
      };
    } else {
      this.callbacks.onDisconnect = [];
    }
  }
}

// Create singleton instance
const frontDeskWebSocket = new FrontDeskWebSocketClient();

export default frontDeskWebSocket; 